import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import {Formik, FormikConsumer, useFormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {Pure} from "../../../../components/Pure";
import Toggle from "../../../../components/Toggle";
import {catholicHouseholdFormSchema, RelationshipType} from "./catholicHouseholdFormSchema";
import {formatDate, getDateString, parseDayjs} from "../../../../helpers/datetimeHelpers";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {SacramentItem} from "./CatholicHouseholdComponents";
import {GiIronCross} from "react-icons/gi";
import {Avatar, Button, Tabs} from "antd";
import {RiUser2Fill} from "react-icons/ri";
import {BsChevronDown} from "react-icons/bs";
import {renderIf, renderIfElse} from "../../../Shared";
import EmptyHolder from "../../../../components/EmptyHolder";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {IoCloseCircleOutline} from "react-icons/io5";
import TooltipApp from "../../../../components/TooltipApp";
import getLabelOfGender from "../../../../helpers/getLabelOfGender";
import {GoDotFill} from "react-icons/go";


const memberSacramentList = [
    {
        value: 'baptism',
        label: 'sacrament of baptism',
        formSchema: [
            catholicHouseholdFormSchema.baptism__date__church_sacrament,
            catholicHouseholdFormSchema.baptism__parish__source_id_sacrament,
            catholicHouseholdFormSchema.baptism__officiant__participant_sacrament,
        ],
        key: 'baptism_eff_date',
    },
    {
        value: 'first_communion',
        label: 'first communion',
        formSchema: [
            catholicHouseholdFormSchema.first_communion__date__church_sacrament,
            catholicHouseholdFormSchema.first_communion__parish__source_id_sacrament,
            catholicHouseholdFormSchema.first_communion__officiant__participant_sacrament,
        ],
        key: 'first_communion_eff_date',
    },
    {
        value: 'confirmation',
        label: 'sacrament of confirmation',
        formSchema: [
            catholicHouseholdFormSchema.confirmation__date__church_sacrament,
            catholicHouseholdFormSchema.confirmation__parish__source_id_sacrament,
            catholicHouseholdFormSchema.confirmation__officiant__participant_sacrament,
        ],
        key: 'confirmation_eff_date',
    },
    {
        value: 'matrimony',
        label: 'sacrament of matrimony',
        formSchema: [
            catholicHouseholdFormSchema.matrimony__date__church_sacrament,
            catholicHouseholdFormSchema.matrimony__parish__source_id_sacrament,
            catholicHouseholdFormSchema.matrimony__officiant__participant_sacrament,
        ],
        key: 'matrimony_eff_date',
    },
    {
        value: 'holy_orders',
        label: 'sacrament of holy orders',
        formSchema: [
            catholicHouseholdFormSchema.holy_orders__date__church_sacrament,
            catholicHouseholdFormSchema.holy_orders__officiant__parish_sacrament,
        ],
        key: 'holy_orders_eff_date',
    },
]

const MobileSubmitBar = ({submitText}) => {
    const t = useTranslate()
    const {handleSubmit, isValid, isSubmitting} = useFormikContext()
    return (
        <div className="border-t border-color-50 pt-3 pb-1">
            <Button
                type="primary"
                onClick={handleSubmit}
                disabled={!isValid}
                loading={isSubmitting}
                className="w-full button-rounded-lg no-border font-bold"
                style={{height: 48, fontSize: 15}}>
                {t(submitText)}
            </Button>
        </div>
    )
}

export const DetailViewForm = ({item, catholicHousehold, onCancel}) => {
    const t = useTranslate()
    const [current, setCurrent] = useState(0)
    const {patron_saint_name, name, father_name, mother_name, birth_province_name, birth_date, death_date, gender, sacraments = [], member_user, relationship_type} = item ?? {}
    const relation_type = _.find(RelationshipType, {value: relationship_type})?.label

    const displayName = name || member_user?.name
    const fullDisplayName = [patron_saint_name, displayName].filter(Boolean).join(' ')

    const InfoRow = ({label, value}) => {
        if (!value) return null
        return (
            <div className="flex items-baseline gap-3 py-2.5 border-b border-color-50 last:border-b-0">
                <span className="text-xs font-semibold text-color-400 uppercase tracking-wider flex-shrink-0" style={{minWidth: 110}}>
                    {label}
                </span>
                <span className="text-sm font-medium text-color-100 flex-1">{value}</span>
            </div>
        )
    }

    const InformationTab = () => (
        <div className="flex flex-col px-4 pb-3">
            <InfoRow label={t('date of birth')} value={birth_date ? formatDate(birth_date, 'DD/MM/YYYY') : null}/>
            <InfoRow label={t('place of birth')} value={birth_province_name}/>
            {renderIf(death_date, <InfoRow label={t('date of death')} value={formatDate(death_date, 'DD/MM/YYYY')}/>)}
            <InfoRow label={t('gender')} value={t(getLabelOfGender(gender))}/>
            {renderIf(
                relationship_type !== 'CHILDREN',
                <>
                    <InfoRow label={t('father')} value={father_name}/>
                    <InfoRow label={t('mother')} value={mother_name}/>
                </>
            )}
        </div>
    )

    const SacramentsTab = () => (
        renderIfElse(
            sacraments.length > 0,
            <div className="flex flex-col gap-2 px-4 pb-3">
                {sacraments.map((e, i) => (
                    <SacramentItem key={i} item={e}/>
                ))}
            </div>,
            <div className="px-4 pb-3"><EmptyHolder/></div>
        )
    )

    const tabs = [
        {key: 0, label: 'information', content: <InformationTab/>},
        {key: 1, label: 'sacrament', content: <SacramentsTab/>},
    ]

    return (
        <div className="flex flex-col">
            <div className="flex flex-col items-center gap-2 px-6 pt-5 pb-4 border-b border-color-50">
                <Avatar
                    size={56}
                    src={member_user?.avatar}
                    icon={<RiUser2Fill size={28} className="text-color-500"/>}
                    className="flex flex-center background-200 rounded-xl flex-shrink-0"
                />
                <div className="flex flex-col items-center gap-1 text-center">
                    {renderIfElse(
                        name,
                        <span className="font-bold text-xl text-blue-500 uppercase tracking-wider leading-tight">
                            {fullDisplayName}
                        </span>,
                        <Link
                            to={getLinkToDetail(member_user)}
                            className="font-bold text-xl text-blue-500 uppercase tracking-wider leading-tight no-underline">
                            {fullDisplayName}
                        </Link>
                    )}
                    <span className="text-sm font-medium text-color-400 italic">{t(relation_type)}</span>
                    {renderIf(
                        catholicHousehold?.title || catholicHousehold?.source_idcode,
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <GiIronCross size={10} className="text-primary-300"/>
                            <span className="text-xs font-semibold text-primary-400">
                                {catholicHousehold?.title || catholicHousehold?.source_idcode}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <Tabs
                centered
                size="small"
                activeKey={current}
                onChange={setCurrent}
                className="custom-tabs-menu"
                items={tabs.map((e) => ({
                    key: e.key,
                    label: <span className="font-medium text-sm">{t(e.label)}</span>,
                    children: e.content,
                }))}
            />
            <div className="px-4 pb-3 text-right border-t border-color-50">
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-sm font-medium text-color-300 hover:text-color-100 transition-colors py-2">
                    {t('close')}
                </button>
            </div>
        </div>
    )
}

export const CreateCatholicHouseholdMemberForm = ({
                                                      catholicHousehold,
                                                      onCancel = NullAsDefault,
                                                      onSubmit = NullAsDefault,
                                                  }) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}
        _.forEach(['name'], (name) => {
            const value = _.get(values, name)
            if (_.isEmpty(value ?? undefined)) {
                errors[name] = t('required field')
            }
        })
        return errors
    }

    const initialValues = {
        name: undefined,
        catholic_household_id: catholicHousehold?.id,
        relationship_type: (!catholicHousehold?.household_father ? 'FATHER' : (!catholicHousehold?.household_mother ? 'MOTHER' : 'CHILDREN'))
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={({
                           birth_date,
                           baptism_eff_date, first_communion_eff_date, confirmation_eff_date, matrimony_eff_date, holy_orders_eff_date,
                           patron_saint, member_user, birth_province,
                           baptism_church, baptism_parish, baptism_diocese, baptism_officiant_user, baptism_participant_user,
                           first_communion_church, first_communion_parish, first_communion_diocese, first_communion_officiant_user,
                           confirmation_church, confirmation_parish, confirmation_diocese, confirmation_officiant_user,
                           matrimony_church, matrimony_parish, matrimony_diocese, matrimony_officiant_user, matrimony_participant_user,
                           holy_orders_church, holy_orders_parish, holy_orders_diocese, holy_orders_officiant_user, holy_orders_participant_user,
                           ...values
                       }) => {
                onSubmit(_.omitBy(
                    {
                        ...values,
                        ...(!!birth_date ? {birth_date: getDateString(birth_date)} : {}),
                        ...(!!baptism_eff_date ? {baptism_eff_date: getDateString(baptism_eff_date)} : {}),
                        ...(!!first_communion_eff_date ? {first_communion_eff_date: getDateString(first_communion_eff_date)} : {}),
                        ...(!!confirmation_eff_date ? {confirmation_eff_date: getDateString(confirmation_eff_date)} : {}),
                        ...(!!matrimony_eff_date ? {matrimony_eff_date: getDateString(matrimony_eff_date)} : {}),
                        ...(!!holy_orders_eff_date ? {holy_orders_eff_date: getDateString(holy_orders_eff_date)} : {}),
                    },
                    _.isUndefined
                ))
            }}>
            <div className="flex flex-col gap-5 px-4 py-3">
                <div className="flex flex-col gap-4">
                    <HeaderWithIconLine
                        icon={<GoDotFill size={11} className="text-color-300"/>}
                        lineColor="border-color-100"
                        title="member information"
                        className="text-xs font-semibold text-color-400 uppercase tracking-wider"
                    />
                    <Pure>
                        <FieldsFactory
                            formSchema={[
                                catholicHouseholdFormSchema.relationship_type,
                                catholicHouseholdFormSchema.full_name_only,
                                catholicHouseholdFormSchema.patron_saint__feast_day,
                                catholicHouseholdFormSchema.date__place__gender,
                                catholicHouseholdFormSchema.parent,
                            ]}
                        />
                    </Pure>
                </div>

                <div className="flex flex-col gap-2">
                    <HeaderWithIconLine
                        icon={<GoDotFill size={11} className="text-color-300"/>}
                        lineColor="border-color-100"
                        title="sacraments"
                        className="text-xs font-semibold text-color-400 uppercase tracking-wider"
                    />
                    {memberSacramentList.map((se, si) => (
                        <Toggle key={si} defaultValue={false}>
                            {(isToggle, toggle) => (
                                <div className="border border-color-100 rounded-xl overflow-hidden">
                                    <div
                                        onClick={() => toggle(!isToggle)}
                                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:background-50 transition-colors select-none">
                                        <div className="flex items-center gap-2">
                                            <GoDotFill
                                                size={10}
                                                className={isToggle ? 'text-blue-400' : 'text-color-200'}
                                            />
                                            <span className={classNames(
                                                'font-semibold text-xs uppercase tracking-wider',
                                                isToggle ? 'text-blue-500' : 'text-color-400'
                                            )}>
                                                {t(se.label)}
                                            </span>
                                        </div>
                                        <BsChevronDown
                                            size={12}
                                            style={{
                                                transform: isToggle ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                            }}
                                            className="text-color-400"
                                        />
                                    </div>
                                    <div
                                        className="overflow-hidden"
                                        style={{maxHeight: isToggle ? 800 : 0, transition: 'max-height 0.25s ease'}}>
                                        <div className="px-4 pt-2 pb-4 border-t border-color-50">
                                            <Pure>
                                                <FieldsFactory formSchema={se.formSchema}/>
                                            </Pure>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Toggle>
                    ))}
                </div>

                <MobileSubmitBar submitText="create"/>
            </div>
        </Formik>
    )
}

export const EditCatholicHouseholdMemberForm = ({
                                                    item,
                                                    initialValues,
                                                    onCancel = NullAsDefault,
                                                    onSubmit = NullAsDefault,
                                                }) => {
    const t = useTranslate()

    const {sacraments = []} = initialValues;
    const baptism = _.find(sacraments, {
        sacrament_type: 'baptism',
    })
    const first_communion = _.find(sacraments, {
        sacrament_type: 'first_communion',
    })
    const confirmation = _.find(sacraments, {
        sacrament_type: 'confirmation',
    })
    const matrimony = _.find(sacraments, {
        sacrament_type: 'matrimony',
    })
    const holy_orders = _.find(sacraments, {
        sacrament_type: 'holy_orders',
    })
    const _initialValues = {
        ...initialValues,
        gender: Number(initialValues?.gender),
        patron_saint: initialValues?.patron_saint,
        patron_saint_id: initialValues?.patron_saint?.id,
        patron_saint_name: initialValues?.patron_saint?.common_name || initialValues?.patron_saint_name,
        patron_saint_feast_day: initialValues?.patron_saint_feast_day || initialValues?.patron_saint?.feast_day,
        member_user: initialValues?.member_user,
        member_user_id: initialValues?.member_user?.id,
        name: initialValues?.name || initialValues?.member_user?.name,
        birth_province: initialValues?.birth_province,
        birth_province_id: initialValues?.birth_province?.id,
        birth_province_name: initialValues?.birth_province_name || initialValues?.birth_province?.name,
        birth_date: parseDayjs(initialValues?.birth_date),
        death_date: parseDayjs(initialValues?.death_date),

        relationship_type: initialValues?.relationship_type,

        baptism_eff_date: parseDayjs(baptism?.eff_date),
        baptism_church_id: baptism?.church?.id,
        baptism_church_name: baptism?.church_name || baptism?.church?.name,
        baptism_church: baptism?.church,
        baptism_parish_id: baptism?.parish?.id,
        baptism_parish_name: baptism?.parish_name || baptism?.parish?.name,
        baptism_parish: baptism?.parish,
        baptism_diocese_id: baptism?.diocese?.id,
        baptism_diocese_name: baptism?.diocese_name || baptism?.diocese?.name,
        baptism_diocese: baptism?.diocese,
        baptism_source_idcode: baptism?.source_idcode,
        baptism_officiant_user: baptism?.officiant_user,
        baptism_officiant_user_id: baptism?.officiant_user?.id,
        baptism_officiant_name: baptism?.officiant_name || baptism?.officiant_user?.name,
        baptism_participant_user: baptism?.participant_user,
        baptism_participant_user_id: baptism?.participant_user?.id,
        baptism_participant_name: baptism?.participant_name || baptism?.participant_user?.name,

        first_communion_eff_date: parseDayjs(first_communion?.eff_date),
        first_communion_church_id: first_communion?.church?.id,
        first_communion_church_name: first_communion?.church_name || first_communion?.church?.name,
        first_communion_church: first_communion?.church,
        first_communion_parish_id: first_communion?.parish?.id,
        first_communion_parish_name: first_communion?.parish_name || first_communion?.parish?.name,
        first_communion_parish: first_communion?.parish,
        first_communion_diocese_id: first_communion?.diocese?.id,
        first_communion_diocese_name: first_communion?.diocese_name || first_communion?.diocese?.name,
        first_communion_diocese: first_communion?.diocese,
        first_communion_source_idcode: first_communion?.source_idcode,
        first_communion_officiant_user: first_communion?.officiant_user,
        first_communion_officiant_user_id: first_communion?.officiant_user?.id,
        first_communion_officiant_name: first_communion?.officiant_name || first_communion?.officiant_user?.name,
        // first_communion_participant_user: first_communion?.participant_user,
        // first_communion_participant_user_id: first_communion?.participant_user?.id,
        // first_communion_participant_name: first_communion?.participant_name || first_communion?.participant_user?.name,

        confirmation_eff_date: parseDayjs(confirmation?.eff_date),
        confirmation_church_id: confirmation?.church?.id,
        confirmation_church_name: confirmation?.church_name || confirmation?.church?.name,
        confirmation_church: confirmation?.church,
        confirmation_parish_id: confirmation?.parish?.id,
        confirmation_parish_name: confirmation?.parish_name || confirmation?.parish?.name,
        confirmation_parish: confirmation?.parish,
        confirmation_diocese_id: confirmation?.diocese?.id,
        confirmation_diocese_name: confirmation?.diocese_name || confirmation?.diocese?.name,
        confirmation_diocese: confirmation?.diocese,
        confirmation_source_idcode: confirmation?.source_idcode,
        confirmation_officiant_user: confirmation?.officiant_user,
        confirmation_officiant_user_id: confirmation?.officiant_user?.id,
        confirmation_officiant_name: confirmation?.officiant_name || confirmation?.officiant_user?.name,
        confirmation_participant_user: confirmation?.participant_user,
        confirmation_participant_user_id: confirmation?.participant_user?.id,
        confirmation_participant_name: confirmation?.participant_name || confirmation?.participant_user?.name,

        matrimony_eff_date: parseDayjs(matrimony?.eff_date),
        matrimony_church_id: matrimony?.church?.id,
        matrimony_church_name: matrimony?.church_name || matrimony?.church?.name,
        matrimony_church: matrimony?.church,
        matrimony_parish_id: matrimony?.parish?.id,
        matrimony_parish_name: matrimony?.parish_name || matrimony?.parish?.name,
        matrimony_parish: matrimony?.parish,
        matrimony_diocese_id: matrimony?.diocese?.id,
        matrimony_diocese_name: matrimony?.diocese_name || matrimony?.diocese?.name,
        matrimony_diocese: matrimony?.diocese,
        matrimony_source_idcode: matrimony?.source_idcode,
        matrimony_officiant_user: matrimony?.officiant_user,
        matrimony_officiant_user_id: matrimony?.officiant_user?.id,
        matrimony_officiant_name: matrimony?.officiant_name || matrimony?.officiant_user?.name,
        matrimony_participant_user: matrimony?.participant_user,
        matrimony_participant_user_id: matrimony?.participant_user?.id,
        matrimony_participant_name: matrimony?.participant_name || matrimony?.participant_user?.name,

        holy_orders_eff_date: parseDayjs(holy_orders?.eff_date),
        holy_orders_church_id: holy_orders?.church?.id,
        holy_orders_church_name: holy_orders?.church_name || holy_orders?.church?.name,
        holy_orders_church: holy_orders?.church,
        holy_orders_parish_id: holy_orders?.parish?.id,
        holy_orders_parish_name: holy_orders?.parish_name || holy_orders?.parish?.name,
        holy_orders_parish: holy_orders?.parish,
        holy_orders_diocese_id: holy_orders?.diocese?.id,
        holy_orders_diocese_name: holy_orders?.diocese_name || holy_orders?.diocese?.name,
        holy_orders_diocese: holy_orders?.diocese,
        holy_orders_source_idcode: holy_orders?.source_idcode,
        holy_orders_officiant_user: holy_orders?.officiant_user,
        holy_orders_officiant_user_id: holy_orders?.officiant_user?.id,
        holy_orders_officiant_name: holy_orders?.officiant_name || holy_orders?.officiant_user?.name,
        holy_orders_participant_user: holy_orders?.participant_user,
        holy_orders_participant_user_id: holy_orders?.participant_user?.id,
        holy_orders_participant_name: holy_orders?.participant_name || holy_orders?.participant_user?.name,
    }

    const validate = (values) => {
        let errors = {}
        _.forEach(['name'], (name) => {
            const value = _.get(values, name)
            if (_.isEmpty(value ?? undefined)) {
                errors[name] = t('required field')
            }
        })
        return errors
    }

    return (
        <Formik
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={({
                           birth_date, death_date,
                           baptism_eff_date, first_communion_eff_date, confirmation_eff_date, matrimony_eff_date, holy_orders_eff_date,
                           patron_saint, member_user, birth_province,
                           baptism_church, baptism_parish, baptism_diocese, baptism_officiant_user, baptism_participant_user,
                           first_communion_church, first_communion_parish, first_communion_diocese, first_communion_officiant_user,
                           confirmation_church, confirmation_parish, confirmation_diocese, confirmation_officiant_user, confirmation_participant_user,
                           matrimony_church, matrimony_parish, matrimony_diocese, matrimony_officiant_user, matrimony_participant_user,
                           holy_orders_church, holy_orders_parish, holy_orders_diocese, holy_orders_officiant_user, holy_orders_participant_user,
                           ...values
                       }) => {
                const changes = shallowDiff(values, initialValues)
                onSubmit(_.omitBy(
                    {
                        ...changes,
                        ...(!!birth_date ? {birth_date: getDateString(birth_date)} : {}),
                        ...(!!death_date ? {death_date: getDateString(death_date)} : {}),
                        ...(!!baptism_eff_date ? {baptism_eff_date: getDateString(baptism_eff_date)} : {baptism_eff_date: null}),
                        ...(!!first_communion_eff_date ? {first_communion_eff_date: getDateString(first_communion_eff_date)} : {first_communion_eff_date: null}),
                        ...(!!confirmation_eff_date ? {confirmation_eff_date: getDateString(confirmation_eff_date)} : {confirmation_eff_date: null}),
                        ...(!!matrimony_eff_date ? {matrimony_eff_date: getDateString(matrimony_eff_date)} : {matrimony_eff_date: null}),
                        ...(!!holy_orders_eff_date ? {holy_orders_eff_date: getDateString(holy_orders_eff_date)} : {holy_orders_eff_date: null}),
                    },
                    _.isUndefined
                ))
            }}>
            <div className="flex flex-col gap-5 px-4 py-3">
                <div className="flex flex-col gap-4">
                    <HeaderWithIconLine
                        icon={<GoDotFill size={11} className="text-color-300"/>}
                        lineColor="border-color-100"
                        title="member information"
                        className="text-xs font-semibold text-color-400 uppercase tracking-wider"
                    />
                    <Pure>
                        <FieldsFactory
                            formSchema={[
                                catholicHouseholdFormSchema.relationship_type,
                                catholicHouseholdFormSchema.full_name_only,
                                catholicHouseholdFormSchema.patron_saint__feast_day,
                                catholicHouseholdFormSchema.date__place__gender,
                                catholicHouseholdFormSchema.death_date,
                                catholicHouseholdFormSchema.parent,
                            ]}
                        />
                    </Pure>
                </div>

                <div className="flex flex-col gap-2">
                    <HeaderWithIconLine
                        icon={<GoDotFill size={11} className="text-color-300"/>}
                        lineColor="border-color-100"
                        title="sacraments"
                        className="text-xs font-semibold text-color-400 uppercase tracking-wider"
                    />
                <FormikConsumer>
                    {({values, errors, handleChange, setValues, ...rest}) => (
                        [
                            {
                                value: 'baptism',
                                label: 'sacrament of baptism',
                                formSchema: [
                                    catholicHouseholdFormSchema.baptism__date__church_sacrament,
                                    catholicHouseholdFormSchema.baptism__parish__source_id_sacrament,
                                    catholicHouseholdFormSchema.baptism__officiant__participant_sacrament,
                                ],
                                key: 'baptism_eff_date',
                                clearSacrament: () => {
                                    setValues(
                                        {
                                            ...values,
                                            baptism_source_idcode: null,
                                            baptism_eff_date: null,
                                            baptism_church_id: null,
                                            baptism_church_name: null,
                                            baptism_church: null,
                                            baptism_parish_id: null,
                                            baptism_parish_name: null,
                                            baptism_parish: null,
                                            baptism_diocese_id: null,
                                            baptism_diocese_name: null,
                                            baptism_diocese: null,
                                            baptism_officiant_user: null,
                                            baptism_officiant_user_id: null,
                                            baptism_officiant_name: null,
                                            baptism_participant_user: null,
                                            baptism_participant_user_id: null,
                                            baptism_participant_name: null,
                                        }
                                    );
                                }
                            },
                            {
                                value: 'first_communion',
                                label: 'first communion',
                                formSchema: [
                                    catholicHouseholdFormSchema.first_communion__date__church_sacrament,
                                    catholicHouseholdFormSchema.first_communion__parish__source_id_sacrament,
                                    catholicHouseholdFormSchema.first_communion__officiant__participant_sacrament,
                                ],
                                key: 'first_communion_eff_date',
                                clearSacrament: () => {
                                    setValues(
                                        {
                                            ...values,
                                            first_communion_source_idcode: null,
                                            first_communion_eff_date: null,
                                            first_communion_church_id: null,
                                            first_communion_church_name: null,
                                            first_communion_church: null,
                                            first_communion_parish_id: null,
                                            first_communion_parish_name: null,
                                            first_communion_parish: null,
                                            first_communion_diocese_id: null,
                                            first_communion_diocese_name: null,
                                            first_communion_diocese: null,
                                            first_communion_officiant_user: null,
                                            first_communion_officiant_user_id: null,
                                            first_communion_officiant_name: null,
                                            // first_communion_participant_user: null, first_communion_participant_user_id: null, first_communion_participant_name: null,
                                        }
                                    );
                                }
                            },
                            {
                                value: 'confirmation',
                                label: 'sacrament of confirmation',
                                formSchema: [
                                    catholicHouseholdFormSchema.confirmation__date__church_sacrament,
                                    catholicHouseholdFormSchema.confirmation__parish__source_id_sacrament,
                                    catholicHouseholdFormSchema.confirmation__officiant__participant_sacrament,
                                ],
                                key: 'confirmation_eff_date',
                                clearSacrament: () => {
                                    setValues(
                                        {
                                            ...values,
                                            confirmation_source_idcode: null,
                                            confirmation_eff_date: null,
                                            confirmation_church_id: null,
                                            confirmation_church_name: null,
                                            confirmation_church: null,
                                            confirmation_parish_id: null,
                                            confirmation_parish_name: null,
                                            confirmation_parish: null,
                                            confirmation_diocese_id: null,
                                            confirmation_diocese_name: null,
                                            confirmation_diocese: null,
                                            confirmation_officiant_user: null,
                                            confirmation_officiant_user_id: null,
                                            confirmation_officiant_name: null,
                                            confirmation_participant_user: null,
                                            confirmation_participant_user_id: null,
                                            confirmation_participant_name: null,
                                        }
                                    );
                                }
                            },
                            {
                                value: 'matrimony',
                                label: 'sacrament of matrimony',
                                formSchema: [
                                    catholicHouseholdFormSchema.matrimony__date__church_sacrament,
                                    catholicHouseholdFormSchema.matrimony__parish__source_id_sacrament,
                                    catholicHouseholdFormSchema.matrimony__officiant__participant_sacrament,
                                ],
                                key: 'matrimony_eff_date',
                                clearSacrament: () => {
                                    setValues(
                                        {
                                            ...values,
                                            matrimony_source_idcode: null,
                                            matrimony_eff_date: null,
                                            matrimony_church_id: null,
                                            matrimony_church_name: null,
                                            matrimony_church: null,
                                            matrimony_parish_id: null,
                                            matrimony_parish_name: null,
                                            matrimony_parish: null,
                                            matrimony_diocese_id: null,
                                            matrimony_diocese_name: null,
                                            matrimony_diocese: null,
                                            matrimony_officiant_user: null,
                                            matrimony_officiant_user_id: null,
                                            matrimony_officiant_name: null,
                                            matrimony_participant_user: null,
                                            matrimony_participant_user_id: null,
                                            matrimony_participant_name: null,
                                        }
                                    );
                                }
                            },
                            {
                                value: 'holy_orders',
                                label: 'sacrament of holy orders',
                                formSchema: [
                                    catholicHouseholdFormSchema.holy_orders__date__church_sacrament,
                                    catholicHouseholdFormSchema.holy_orders__officiant__parish_sacrament,
                                ],
                                key: 'holy_orders_eff_date',
                                clearSacrament: () => {
                                    setValues(
                                        {
                                            ...values,
                                            holy_orders_source_idcode: null,
                                            holy_orders_eff_date: null,
                                            holy_orders_church_id: null,
                                            holy_orders_church_name: null,
                                            holy_orders_church: null,
                                            holy_orders_parish_id: null,
                                            holy_orders_parish_name: null,
                                            holy_orders_parish: null,
                                            holy_orders_diocese_id: null,
                                            holy_orders_diocese_name: null,
                                            holy_orders_diocese: null,
                                            holy_orders_officiant_user: null,
                                            holy_orders_officiant_user_id: null,
                                            holy_orders_officiant_name: null,
                                            holy_orders_participant_user: null,
                                            holy_orders_participant_user_id: null,
                                            holy_orders_participant_name: null,
                                        }
                                    );
                                }
                            },
                        ].map((se, si) => (
                            <Toggle key={si} defaultValue={!!_.get(values, se.key)}>
                                {(isToggle, toggle) => (
                                    <div className="border border-color-100 rounded-xl overflow-hidden">
                                        <div className="flex items-center">
                                            <div
                                                onClick={() => toggle(!isToggle)}
                                                className="flex-1 flex items-center justify-between px-4 py-3 cursor-pointer hover:background-50 transition-colors select-none">
                                                <div className="flex items-center gap-2">
                                                    <GoDotFill
                                                        size={10}
                                                        className={isToggle ? 'text-blue-400' : 'text-color-200'}
                                                    />
                                                    <span className={classNames(
                                                        'font-semibold text-xs uppercase tracking-wider',
                                                        isToggle ? 'text-blue-500' : 'text-color-400'
                                                    )}>
                                                        {t(se.label)}
                                                    </span>
                                                </div>
                                                <BsChevronDown
                                                    size={12}
                                                    style={{
                                                        transform: isToggle ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease',
                                                    }}
                                                    className="text-color-400"
                                                />
                                            </div>
                                            {isToggle && (
                                                <div className="pr-3 flex-shrink-0">
                                                    <TooltipApp title={t('delete sacrament')} classNames={{root: "font-medium text-xs italic"}}>
                                                        <div
                                                            onClick={() => {
                                                                showDeleteConfirmDialog({
                                                                    translate: t,
                                                                    title: 'delete sacrament',
                                                                    message: 'are you sure you want to delete this sacrament',
                                                                    onOk: () => { se.clearSacrament() }
                                                                })
                                                            }}
                                                            style={{width: 24, height: 24, borderRadius: 5}}
                                                            className="flex flex-center bg-primary-50 hover:bg-primary-100 text-primary cursor-pointer">
                                                            <IoCloseCircleOutline size={12}/>
                                                        </div>
                                                    </TooltipApp>
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            className="overflow-hidden"
                                            style={{maxHeight: isToggle ? 800 : 0, transition: 'max-height 0.25s ease'}}>
                                            <div className="px-4 pt-2 pb-4 border-t border-color-50">
                                                <Pure>
                                                    <FieldsFactory formSchema={se.formSchema}/>
                                                </Pure>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Toggle>
                        ))
                    )}
                </FormikConsumer>
                </div>

                <MobileSubmitBar submitText="save"/>
            </div>
        </Formik>
    )
}

